import {
  Divider,
  EHeadingStyleVariant,
  ELoaderSize,
  ETextStyleVariant,
  EToastVariant,
  Heading,
  Loader,
  Toggle,
} from '@outdoorsyco/bonfire';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToast } from '@/hooks/useToast';
import {
  useUpdateUserIterableNotifications,
  useUpdateUserNotifications,
  useUserIterableNotifications,
} from '@/services/getUserData';
import { IFullUserInfo } from '@/services/types/user/user';

import { getEmailOptions, getPushOptions, getSMSIterableOptions } from './utils';

export enum EIterableMessageType {
  // Email notifications
  GuestNewsletter = '129045',
  GuestPromotions = '129047',
  GuestCampgroundPromotions = '131142',
  BayfieldNewsletter = '131140',
  TexasHillCountryNewsletter = '131141',
  YosemiteNewsletter = '131139',

  // SMS notifications
  GuestSMSPromotions = '85376',

  // Push notifications
  GuestPushPromotions = '131130',
}

type TNotificationsProps = {
  userData: IFullUserInfo;
  refreshUser: () => void;
};

export const Notifications = ({ userData, refreshUser }: TNotificationsProps) => {
  const intl = useIntl();
  const { showToast } = useToast();

  const [smsBookingsAlertsEnabled, setBookingsSmsAlertsEnabled] = useState(false);
  const [smsMessagesAlertsEnabled, setMessagesSmsAlertsEnabled] = useState(false);
  const [unsubscribedMessageIds, setUnsubscribedMessageIds] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: userCardResponse,
    mutate: refreshUserNotifications,
    isLoading,
  } = useUserIterableNotifications(userData?.id);

  const unsubscribedMessageTypeIds = userCardResponse?.unsubscribed_message_type_ids;

  const {
    mutate: updateUserIterableNotifications,
    isValidating: isUpdatingUserIterableNotifications,
  } = useUpdateUserIterableNotifications(userData?.id);

  const { mutate: updateUserNotifications, isValidating: isUpdatingUserNotifications } =
    useUpdateUserNotifications(userData?.id);

  const bookingNotificationLabel = intl.formatMessage({
    defaultMessage: 'Booking notifications',
    id: 'KxCopT',
  });
  const messageNotificationLabel = intl.formatMessage({
    defaultMessage: 'Message notifications',
    id: 'Vp9yG/',
  });

  useEffect(() => {
    if (userCardResponse) {
      if (unsubscribedMessageTypeIds && unsubscribedMessageTypeIds.length > 0) {
        setUnsubscribedMessageIds(
          unsubscribedMessageTypeIds.map((messageId: number) => {
            return messageId.toString();
          }),
        );
      } else {
        setUnsubscribedMessageIds([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCardResponse]);

  useEffect(() => {
    if (userData) {
      setBookingsSmsAlertsEnabled(!!userData.profile.notifications.sms.bookings);
      setMessagesSmsAlertsEnabled(!!userData.profile.notifications.sms.messages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.profile.disable_phone, userData.profile.notifications]);

  if (isLoading) {
    return (
      <div className="text-center">
        <Loader size={ELoaderSize.Large} />
      </div>
    );
  }

  const isSubscribedForMessageId = (messageId: string): boolean => {
    return unsubscribedMessageIds.findIndex((id: string) => id === messageId) === -1;
  };

  const toggleSmsNotifications = async (enabled: boolean, field: 'bookings' | 'messages') => {
    setIsUpdating(true);
    if (field === 'bookings') {
      setBookingsSmsAlertsEnabled(enabled);
    }
    if (field === 'messages') {
      setMessagesSmsAlertsEnabled(enabled);
    }

    try {
      await updateUserNotifications({
        ...userData.profile.notifications,
        sms: {
          bookings: field === 'bookings' ? enabled : smsBookingsAlertsEnabled,
          messages: field === 'messages' ? enabled : smsMessagesAlertsEnabled,
        },
      });
      await refreshUser();
    } catch {
      await refreshUser();

      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Failed to save notification settings.',
          id: 'F+agE5',
        }),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleIterableNotifications = async (
    iterableMessageId: EIterableMessageType,
    enabled: boolean,
  ) => {
    setIsUpdating(true);
    const unsubscribedMessageIdsForUpdate: string[] = [];

    if (enabled) {
      removeUnsubscribeMessageId(
        unsubscribedMessageIds,
        unsubscribedMessageIdsForUpdate,
        iterableMessageId,
      );
    } else {
      addUnsubscribeMessageId(
        unsubscribedMessageIds,
        unsubscribedMessageIdsForUpdate,
        iterableMessageId,
      );
    }

    const uniqueMessages = unsubscribedMessageIdsForUpdate.filter(
      (item, index) => unsubscribedMessageIdsForUpdate.indexOf(item) === index,
    );
    setUnsubscribedMessageIds(uniqueMessages);

    try {
      await updateUserIterableNotifications({
        id: userData?.id.toString(),
        unsubscribed_message_type_ids: unsubscribedMessageIdsForUpdate.map((id: string) =>
          parseInt(id),
        ),
      });
    } catch {
      await refreshUserNotifications();

      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Failed to save notification settings.',
          id: 'F+agE5',
        }),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const isDisabled =
    isUpdatingUserIterableNotifications || isUpdatingUserNotifications || isUpdating;

  return (
    <div>
      <Heading variant={EHeadingStyleVariant.H5} className="mb-10">
        <FormattedMessage defaultMessage="Notifications" id="NAidKb" />
      </Heading>
      <div className="flex flex-col mb-8 gap-4">
        <div className="flex flex-col gap-6">
          <Heading variant={EHeadingStyleVariant.H6}>
            <FormattedMessage defaultMessage="Email notifications" id="3hYpLY" />
          </Heading>
          <div className="flex flex-col gap-6">
            {getEmailOptions(intl).map(option => (
              <Toggle
                contentOnLeft
                key={option.id}
                disabled={isDisabled}
                ariaLabel={option.label}
                className="flex items-center w-full md:justify-between"
                contentClassName="[&>p]:!text-secondary-base [&>p]:!text-base"
                labelVariant={ETextStyleVariant.MediumBold}
                isSelected={isSubscribedForMessageId(option.id)}
                content={{
                  label: option.label,
                  description: option.description,
                }}
                onChange={(value: boolean) => toggleIterableNotifications(option.id, value)}
              />
            ))}
          </div>
        </div>

        <Divider className="my-10" />

        <div className="flex flex-col gap-6">
          <Heading variant={EHeadingStyleVariant.H6}>
            <FormattedMessage defaultMessage="SMS notifications" id="0QxrDG" />
          </Heading>
          <div className="flex flex-col gap-6">
            <Toggle
              contentOnLeft
              disabled={isDisabled}
              ariaLabel={bookingNotificationLabel}
              isSelected={smsBookingsAlertsEnabled}
              className="flex items-center w-full md:justify-between"
              contentClassName="[&>p]:!text-secondary-base [&>p]:!text-base"
              labelVariant={ETextStyleVariant.MediumBold}
              onChange={(value: boolean) => toggleSmsNotifications(value, 'bookings')}
              content={{
                label: bookingNotificationLabel,
                description: intl.formatMessage({
                  defaultMessage: 'Receive alerts for all new booking requests.',
                  id: 'CkD0bb',
                }),
              }}
            />
            <Toggle
              contentOnLeft
              disabled={isDisabled}
              ariaLabel={messageNotificationLabel}
              isSelected={smsMessagesAlertsEnabled}
              className="flex items-center w-full md:justify-between"
              contentClassName="[&>p]:!text-secondary-base [&>p]:!text-base"
              labelVariant={ETextStyleVariant.MediumBold}
              onChange={(value: boolean) => toggleSmsNotifications(value, 'messages')}
              content={{
                label: messageNotificationLabel,
                description: intl.formatMessage({
                  defaultMessage: 'Receive alerts for all new messages.',
                  id: 'kCXVa7',
                }),
              }}
            />
            {getSMSIterableOptions(intl).map(option => (
              <Toggle
                contentOnLeft
                key={option.id}
                disabled={isDisabled}
                ariaLabel={option?.ariaLabel || ''}
                className="flex items-center w-full md:justify-between"
                contentClassName="[&>p]:!text-secondary-base [&>p]:!text-base"
                labelVariant={ETextStyleVariant.MediumBold}
                isSelected={isSubscribedForMessageId(option.id)}
                content={{
                  label: option.label,
                  description: option.description,
                }}
                onChange={(value: boolean) => toggleIterableNotifications(option.id, value)}
              />
            ))}
          </div>
        </div>

        <Divider className="my-10" />

        <div className="flex flex-col gap-6">
          <Heading variant={EHeadingStyleVariant.H6}>
            <FormattedMessage defaultMessage="Push notifications" id="Awq32I" />
          </Heading>
          <div className="flex flex-col gap-6">
            {getPushOptions(intl).map(option => (
              <Toggle
                contentOnLeft
                key={option.id}
                disabled={isDisabled}
                ariaLabel={option?.ariaLabel || ''}
                className="flex items-center w-full md:justify-between"
                contentClassName="[&>p]:!text-secondary-base [&>p]:!text-base"
                labelVariant={ETextStyleVariant.MediumBold}
                isSelected={isSubscribedForMessageId(option.id)}
                content={{
                  label: option.label,
                  description: option.description,
                }}
                onChange={(value: boolean) => toggleIterableNotifications(option.id, value)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const addUnsubscribeMessageId = (
  originalList: string[],
  updatedList: string[],
  messageId: EIterableMessageType,
) => {
  if (originalList.indexOf(messageId) === -1) {
    updatedList.push(...originalList, messageId);
  }
};

const removeUnsubscribeMessageId = (
  originalList: string[],
  updatedList: string[],
  messageId: EIterableMessageType,
) => {
  if (originalList.includes(messageId)) {
    const filteredIds: string[] = originalList.filter((id: string) => id !== messageId);
    updatedList.push(...filteredIds);
  }
};
